import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import { useEffect, useState, MouseEvent, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "../../../components/Filtering/Pagination";
import SnackBar from "../../../components/Alerts/SnackBar";
import useMessage from "../../../hooks/useMessage";

import ListGridToggle from "../../../components/Buttons/ListGridToggle";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { TableData, TableDataField, TableHeader } from "../../../components/Table/DataTable";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import { useGetDataFileDataframeDataFileDataframeDataFileHashGet, useGetDataFilesDataFileGet } from "../../../api/generated/fastAPI";
import { DataFile } from "../../../api/models";
import usePaginate from "../../../hooks/usePaginate";
import { ReactComponent as RecordsIcon } from '../../../assets/Icons/adornments/Records.svg';
import GenerateDatasetModal from "../../Datasets/components/GenerateDatasetModal";
import DeleteModal from "../../../components/Popups/DeleteModal";
import GenerateReadAcrossModal from "../../ReadAcross/components/GenerateReadAcrossModal";
import SingleCompoundPredictionModal from "../../Resultsets/components/SingleCompoundPredictionModal";
import { exportFileExtensions } from "../../../utils/supplementalData";
import { exportData } from "../../../utils/formattingFns";
import useDelete from "../../../hooks/endpoints/useDelete";
import useUpdate from "../../../hooks/endpoints/useUpdate";
import { useAccess } from "../../../access/useAccess";
import { demo_access, readacross_access } from "../../../access/predefinedConditions";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import { formatDisplayedDatafileMetadata } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import VirtualizedGridList from "../../../components/Grid/VirtualizedGridList";

export default function IndividualFile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canAccess } = useAccess();

    const [ headers, setHeaders ] = useState<TableHeader[]>([]);
    const [ isGrid, setIsGrid ] = useState(false);

    const { data: dataframeData, isLoading: isDataframeLoading } = useGetDataFileDataframeDataFileDataframeDataFileHashGet(id ? id : '');
    const { data: datafileData, isLoading: isDatafileLoading, refetch } = useGetDataFilesDataFileGet({ data_file_hash: id ? id : '' })
    
    const [ dataFile, setDataFile ] = useState<DataFile | null>(null);
    const [ dataframe, setDataframe ] = useState<{
        [key: string]: {
            [key: number]: string | number,
        }
    } | null>(null);
    const [ tableData, setTableData ] = useState<TableData[]>([]);

    const [openGenerateDatasetId, setOpenGenerateDatasetId] = useState<string | null | undefined>(null);
    

    const {
        setItems,
        setItemsPerPage,
        setCurrentPage,
        currentPage,
        paginatedItems,
        startIndex,
        endIndex,
        totalPages,
        paginate,
    } = usePaginate();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const datafileMetadata = useMemo(() => {
        if (!dataFile) return [];
        return formatDisplayedDatafileMetadata(dataFile);
    }, [dataFile]);

    useEffect(() => {
        if (datafileData) {
            setDataFile(datafileData as unknown as DataFile);
        }
    }, [datafileData]);

    useEffect(() => {
        if (dataframeData) {
            const data = dataframeData as unknown as {
                [key: string]: {
                    [key: number]: string | number,
                }
            };
            setDataframe(data);
        }
    }, [dataframeData]);

    const { deleteFiles } = useDelete({ setPopupMessage });
    const { updateDataFile } = useUpdate({ 
        setPopupMessage, 
        itemToUpdate: dataFile, 
        refetchFn: () => refetch() 
    });

    useEffect(() => {
        if (dataFile !== null && dataframe !== null) {
            const dfHeaders: TableHeader[] = [];
            const dfData: TableData[] = [];

            let dfKeys: string[] = [];

            Object.keys(dataframe).forEach(dfKey => {
                if (dfKey.toLocaleLowerCase().includes('smiles')) {
                    dfKeys.splice(0, 0, dfKey);
                    dfKeys.splice(1, 0, dfKey);
                } else {
                    dfKeys.push(dfKey);
                }
            });

            dfKeys.forEach((key, i) => {
                let header: TableHeader = {
                    label: key,
                    value: key,
                    isShown: true,
                    alignment: 'center',
                };

                if (key.toLocaleLowerCase().includes('smiles')) {
                    if (i === 0) {
                        header.label = 'Structure'; 
                        header.alignment = 'left';
                    } else {
                        header.label = 'SMILES';
                    }
                }
                
                dfHeaders.push(header);
            });

            const records = Object.keys(Object.values(dataframe)[0]).map(key => Number(key));
            records.forEach(record => {
                const fields: TableDataField[] = [];
        
                dfHeaders.forEach((header, i) => {
                    let field: TableDataField = {
                        associatedColumn: header.value,
                        value: '',
                        type: 'text',
                        alignment: header.alignment,
                    };
        
                    const recordValue = dataframe[header.value][record] ?? '';
                    field.value = recordValue.toString();
        
                    if (header.value.toLocaleLowerCase().includes('smiles') && i === 0) {
                        field.type = 'structure';
                    } 
        
                    fields.push(field);
                });

                const structureField = fields.find(field => field.type === 'structure');

                const options: DropdownOption[] = [];

                if (canAccess({or: readacross_access})) options.push(
                    {
                        label: 'Generate Read-Across',
                        btnComp: (
                            <GenerateReadAcrossModal 
                                setPopupMessage={setPopupMessage}
                                selectedCompound={structureField ? structureField.value as string : undefined}
                                location="dropdown"
                            />
                        )
                    },
                );
                options.push(
                    {
                    label: 'Run Prediction',
                        btnComp: (
                            <SingleCompoundPredictionModal 
                                setPopupMessage={setPopupMessage}
                                selectedCompound={structureField ? structureField.value as string : undefined}
                                location="dropdown"
                            />
                        )
                    }
                );

        
                dfData.push({
                    id: record,
                    fields,
                    options,
                });
            });

            setHeaders(dfHeaders);
            setTableData(dfData);
            setItems(dfData);

        }

        //eslint-disable-next-line
    }, [
        dataFile,
        dataframe,
    ])    

    
    const dropdownOptions = useMemo(() => {
        const options: DropdownOption[] = [
            {
                label: 'Generate Dataset from File',
                action: () => setOpenGenerateDatasetId(id), // Only trigger open
            },
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            const name = dataFile && dataFile.name ? dataFile.name.split('.')[0] : '';
                            try {
                                await exportData(
                                    [{ name, hash: id ? id : '' }],
                                    ext.file_type,
                                    'datafile',
                                );

                                setPopupMessage('You have successfully downloaded the selected file.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this file. Please try again.', false);
                            }
                        }
                    }
                ))
            },
        ];

        if (canAccess({or: demo_access})) options.push({
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'data_file',
                        id: id ? id : '',
                        name: dataFile && dataFile.name ? dataFile.name : '',
                    }]}
                    deleteFn={deleteFiles}
                />
            ),
        });

        return options;

        //eslint-disable-next-line
    }, [
        id,
        dataFile,
    ])



    return (
        <div className="p-10 h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4">
                <GenerateDatasetModal 
                    selectedFileId={openGenerateDatasetId ?? undefined}
                    isDropdownItem
                    setPopupMessage={setPopupMessage}
                    open={!!openGenerateDatasetId}
                    setOpen={(val) => setOpenGenerateDatasetId(val ? openGenerateDatasetId : null)}
                />

                <SinglePageHeader 
                    id='single-file-header' 
                    label={dataFile ? dataFile.name! : ''} 
                    isLoading={isDatafileLoading || isDataframeLoading} 
                    pages={[
                        {
                            label: 'Files',
                            action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/files')}
                        },
                        {
                            label: dataFile ? dataFile.name! : '',
                        }
                    ]}
                    itemMetadata={[{
                        label: 'Records',
                        value: dataFile ? dataFile.datafile_metadata!.records_number!.toString() : '0',
                        adornment: <RecordsIcon/>,
                    }]}
                >
                    <NestedDropdown
                        dir="right"
                        btn={{
                            label: 'Options',
                            type: 'primary',
                            styling: 'solid',
                            id: 'single-file-options'
                        }}
                        options={dropdownOptions}
                    />
                    {datafileMetadata.length > 0 && (
                        <UpdatedMetadataMenu 
                            menuLabel="Datafile Metadata"
                            menuItems={datafileMetadata}
                            updateFunc={updateDataFile}
                            location="page"
                        />
                    )}
                </SinglePageHeader>

                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        {/* <SearchBar/> */}
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={headers} 
                            setHeaders={setHeaders} 
                            isDisabled={false}
                        />
                        <ListGridToggle setIsGrid={setIsGrid} isGrid={isGrid}/>
                    </div>
                </div>

                <>
                {isGrid ? (
                    <>
                        <VirtualizedGridList 
                            isLoading={isDatafileLoading || isDataframeLoading} 
                            gridData={paginatedItems}
                            headers={headers}
                            setPopupMessage={setPopupMessage}
                        />
                    </>
                ) : (
                    <>
                        <VirtualizedDataTable 
                            isLoading={isDatafileLoading || isDataframeLoading} 
                            headers={headers} 
                            tableData={paginatedItems}
                            setPopupMessage={setPopupMessage}
                            fixedItemHeight={194}
                        />                  
                    </>
                    )}

                </>
                
                <Pagination
                    setItemsPerPage={setItemsPerPage}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    totalPages={totalPages}
                    total={tableData.length}
                    paginate={paginate}
                />
                
                <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  


        </div>
    )
}